import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { toast } from '@/components/ui/toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Mail, ShieldAlert, Clipboard } from 'lucide-react';

const TWO_FACTOR_ENROLLMENT_USERS = gql`
  query TwoFactorEnrollmentUsers($filters: TwoFactorEnrollmentFiltersInput) {
    twoFactorEnrollmentUsers(filters: $filters) {
      totalCount
      entries {
        id
        username
        realName
        email
        twofactorEnabled
        twoFactorLabel
        isSsoUser
        isTwoFactorEnrolled
        latestInvite {
          id
          status
          initiatedAt
          initiatedBy
          emailSentAt
          messageSentAt
        }
      }
    }
  }
`;

const SEND_TWO_FACTOR_ENROLLMENT_NOTIFICATION = gql`
  mutation SendTwoFactorEnrollmentNotification($input: TwoFactorEnrollmentNotificationInput!) {
    sendTwoFactorEnrollmentNotification(input: $input) {
      success
      enrollmentUrl
      status
    }
  }
`;

type EnrollmentFilter = 'not_enrolled' | 'enrolled';

interface EnrollmentInvite {
  id: number;
  status: string;
  initiatedAt?: string;
  initiatedBy?: string;
  emailSentAt?: string;
  messageSentAt?: string;
}

interface EnrollmentUser {
  id: number;
  username: string;
  realName?: string;
  email?: string;
  twofactorEnabled: number | null;
  twoFactorLabel: string;
  isSsoUser: boolean;
  isTwoFactorEnrolled: boolean;
  latestInvite?: EnrollmentInvite | null;
}

interface ListResponse {
  twoFactorEnrollmentUsers: {
    totalCount: number;
    entries: EnrollmentUser[];
  };
}

interface ListVariables {
  filters: {
    search: string;
    includeSso: boolean;
    enrollmentState: EnrollmentFilter;
    limit: number;
    offset: number;
  };
}

const DEFAULT_LIMIT = 200;
const DEFAULT_SUBJECT = 'Action Required: Enable Two-Factor Authentication';
const normalizeUserId = (value: unknown) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) return null;
  if (parsed <= 0) return null;
  return parsed;
};

const buildDefaultEmailBody = (name: string) => {
  const greeting = name || 'there';
  return [
    `Hello ${greeting},`,
    '',
    'Please log into the Diversion Database and open your Messages inbox to complete your two-factor enrollment.',
    'Inside your inbox you will find a secure message with a link to launch the enrollment wizard and scan your QR code.',
    '${url}',
    '',
    'Thank you!'
  ].join('\n');
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const InviteStatus = ({ invite }: { invite?: EnrollmentInvite | null }) => {
  if (!invite) return <span className="text-muted-foreground">No invites</span>;
  const badgeVariant = invite.status === 'completed' ? 'secondary' : invite.status === 'pending' ? 'default' : 'outline';
  return (
    <div className="flex flex-col gap-1">
      <Badge variant={badgeVariant}>{invite.status}</Badge>
      <span className="text-xs text-muted-foreground">Sent {formatDate(invite.emailSentAt || invite.initiatedAt)}</span>
    </div>
  );
};

export const TwoFactorEnrollmentPage: React.FC = () => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [enrollmentState, setEnrollmentState] = useState<EnrollmentFilter>('not_enrolled');
  const includeSso = false;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<EnrollmentUser | null>(null);
  const [emailSubject, setEmailSubject] = useState(DEFAULT_SUBJECT);
  const [emailBody, setEmailBody] = useState('');
  const [messageIntro, setMessageIntro] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkSending, setBulkSending] = useState(false);

  const filtersPayload = useMemo(() => ({
    search,
    includeSso,
    enrollmentState,
    limit: DEFAULT_LIMIT,
    offset: 0
  }), [search, enrollmentState]);

  const { data, loading, error, refetch } = useQuery<ListResponse, ListVariables>(
    TWO_FACTOR_ENROLLMENT_USERS,
    {
      variables: { filters: filtersPayload },
      fetchPolicy: 'cache-and-network'
    }
  );

  const [sendNotification, { loading: sending }] = useMutation(
    SEND_TWO_FACTOR_ENROLLMENT_NOTIFICATION
  );

  const users = data?.twoFactorEnrollmentUsers?.entries ?? [];
  const totalCount = data?.twoFactorEnrollmentUsers?.totalCount ?? 0;
  const selectableUsers = users.filter((user) => !user.isTwoFactorEnrolled && !user.isSsoUser);
  const selectableIds = selectableUsers.map((user) => user.id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

  useEffect(() => {
    setSelectedIds([]);
  }, [users]);

  const handleSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSearch(searchInput.trim());
  };

  const toggleSelection = (id: number) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((entry) => entry !== id);
      return [...prev, id];
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(selectableIds);
  };

  const resolveTargets = () => {
    if (selectedIds.length > 0) return selectableUsers.filter((user) => selectedIds.includes(user.id));
    return selectableUsers;
  };

  const handleSelectUser = (user: EnrollmentUser) => {
    setSelectedUser(user);
    setEmailSubject(DEFAULT_SUBJECT);
    setEmailBody(buildDefaultEmailBody(user.realName || user.username));
    setMessageIntro('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleSendNotification = async () => {
    if (!selectedUser) return;
    const userId = normalizeUserId(selectedUser.id);
    if (!userId) {
      toast.error('Invalid target user id');
      return;
    }

    try {
      await sendNotification({
        variables: {
          input: {
            userId,
            emailSubject,
            emailBody,
            messageIntro
          }
        }
      });
      toast.success(`Enrollment instructions sent to ${selectedUser.username}`);
      closeDialog();
      refetch();
    } catch (mutationError) {
      const reason = mutationError instanceof Error ? mutationError.message : 'Unable to send notification';
      toast.error(reason);
    }
  };

  const handleBulkNotify = async () => {
    if (bulkSending) return;
    const targets = resolveTargets();
    if (targets.length === 0) {
      toast.error('No eligible users selected');
      return;
    }

    setBulkSending(true);
    let sent = 0;
    let failed = 0;

    for (const user of targets) {
      const userId = normalizeUserId(user.id);
      if (!userId) {
        failed += 1;
        console.warn(`Bulk notify skipped invalid user id for ${user.username}`, user.id);
        continue;
      }

      try {
        await sendNotification({
          variables: {
            input: {
              userId
            }
          }
        });
        sent += 1;
      } catch (mutationError) {
        failed += 1;
        const reason = mutationError instanceof Error ? mutationError.message : 'Unable to send notification';
        console.warn(`Bulk notify failed for ${user.username}`, reason);
      }
    }

    setBulkSending(false);
    refetch();

    const messageParts = [];
    if (sent > 0) messageParts.push(`Sent ${sent}`);
    if (failed > 0) messageParts.push(`Failed ${failed}`);
    const summary = messageParts.join(', ') || 'No notifications sent';
    toast.success(summary);
  };

  const handleCopyEmails = async () => {
    const targets = resolveTargets();
    const emails = Array.from(new Set(targets.map((user) => user.email).filter(Boolean) as string[]));
    if (emails.length === 0) {
      toast.error('No emails to copy');
      return;
    }

    try {
      await navigator.clipboard.writeText(emails.join(', '));
      toast.success(`Copied ${emails.length} email${emails.length === 1 ? '' : 's'} (comma-separated)`);
    } catch (copyError) {
      const reason = copyError instanceof Error ? copyError.message : 'Unable to copy emails';
      toast.error(reason);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <ShieldAlert className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-2xl font-semibold">Two-Factor Enrollment</h1>
          <p className="text-muted-foreground text-sm">
            Monitor non-SSO users and send enrollment reminders with secure message links.
          </p>
        </div>
      </div>

      <Card className="p-4 space-y-4">
        <form onSubmit={handleSearchSubmit} className="flex flex-wrap gap-3">
          <Input
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            placeholder="Search by name, username, or email"
            className="max-w-sm"
          />
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>

        <div className="flex flex-wrap items-center gap-3 justify-between">
          <Tabs value={enrollmentState} onValueChange={(value) => setEnrollmentState(value as EnrollmentFilter)}>
            <TabsList>
              <TabsTrigger value="not_enrolled">Not Enrolled</TabsTrigger>
              <TabsTrigger value="enrolled">Enrolled</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleCopyEmails}
              disabled={loading || users.length === 0}
              className="gap-2"
            >
              <Clipboard className="h-4 w-4" />
              Copy Emails
            </Button>
            <Button
              type="button"
              onClick={handleBulkNotify}
              disabled={loading || bulkSending || selectableUsers.length === 0}
              className="gap-2"
            >
              {bulkSending && <Loader2 className="h-4 w-4 animate-spin" />}
              Notify Selected
            </Button>
            <div className="text-sm text-muted-foreground">{totalCount} users</div>
          </div>
        </div>
      </Card>

      {error && (
        <Alert variant="destructive">
          <AlertTitle>Unable to load users</AlertTitle>
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      )}

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[48px]">
                <Checkbox
                  aria-label="Select all"
                  checked={allSelected}
                  onCheckedChange={toggleSelectAll}
                  disabled={selectableUsers.length === 0}
                />
              </TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Username</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Two-Factor Method</TableHead>
              <TableHead>Latest Invite</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center">
                  <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                </TableCell>
              </TableRow>
            )}
            {!loading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                  No users match the current filters.
                </TableCell>
              </TableRow>
            )}
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Checkbox
                    aria-label={`Select ${user.username}`}
                    checked={selectedIds.includes(user.id)}
                    onCheckedChange={() => toggleSelection(user.id)}
                    disabled={user.isTwoFactorEnrolled || user.isSsoUser}
                  />
                </TableCell>
                <TableCell>{user.realName || '—'}</TableCell>
                <TableCell className="font-mono text-sm">{user.username}</TableCell>
                <TableCell>{user.email || '—'}</TableCell>
                <TableCell>
                  <Badge variant={user.isTwoFactorEnrolled ? 'secondary' : user.isSsoUser ? 'outline' : 'default'}>
                    {user.twoFactorLabel}
                  </Badge>
                </TableCell>
                <TableCell>
                  <InviteStatus invite={user.latestInvite} />
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    onClick={() => handleSelectUser(user)}
                    disabled={user.isSsoUser || user.isTwoFactorEnrolled || bulkSending}
                    className="gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    Notify
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={(value: boolean) => {
          if (value) {
            setDialogOpen(true);
            return;
          }
          closeDialog();
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Enrollment Instructions</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Sending to {selectedUser.realName || selectedUser.username} ({selectedUser.email || 'no email'})
              </p>
              <div className="space-y-2">
                <Label htmlFor="email-subject">Email subject</Label>
                <Input
                  id="email-subject"
                  value={emailSubject}
                  onChange={(event) => setEmailSubject(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email-body">Email body</Label>
                <Textarea
                  id="email-body"
                  rows={5}
                  value={emailBody}
                  onChange={(event) => setEmailBody(event.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="message-intro">Optional note (appears in secure message)</Label>
                <Textarea
                  id="message-intro"
                  rows={3}
                  placeholder="Add any additional context"
                  value={messageIntro}
                  onChange={(event) => setMessageIntro(event.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button onClick={handleSendNotification} disabled={sending} className="gap-2">
                  {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                  Send Instructions
                </Button>
                <Button variant="outline" onClick={closeDialog} disabled={sending}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
